import { uniqueId } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useEffectOnce } from 'react-use'

type State = Map<string, React.ReactElement>

interface IMountContext {
  unmount: () => void
  update: (props: any) => void
}

const MountContext = React.createContext<IMountContext>({} as any)

let topRender = new Map<
  object,
  [State, React.Dispatch<React.SetStateAction<State>>]
>()

const Render = () => {
  const state = useState<State>(new Map())
  const ref = useRef({ render: uniqueId('render-instance-') })
  const [map] = state

  useEffectOnce(() => {
    const key = ref.current
    topRender.set(key, state)
    return () => {
      topRender.delete(key)
    }
  })

  return <>{Array.from(map.values())}</>
}

export interface IMountProps<P = any> {
  unmount: () => void
  update: (props: P) => void
}

interface IMountOption<P = any> {
  onUpdate?: (data: P) => void
  onUnmount?: () => void
}

export const mount = <P,>(
  element: React.ReactElement<P & { key?: string }>,
  option?: IMountOption<P>
) => {
  if (topRender.size === 0) {
    throw new Error('no render instance')
  }

  let renderInstances = Array.from(topRender.values())
  let [, dispatch] = renderInstances[renderInstances.length - 1]

  const uuid = uniqueId('render-element-')

  const unmount = () => {
    dispatch((map) => {
      map.delete(uuid)
      return new Map(map)
    })
    option?.onUnmount?.()
  }

  const update = (props: P) => {
    dispatch((map) => {
      const oldElement = map.get(uuid)
      if (oldElement) {
        const handler = { unmount, update }
        map.set(
          uuid,
          <MountContext.Provider key={uuid} value={handler}>
            {React.cloneElement(oldElement, {
              ...props,
              ...handler,
            })}
          </MountContext.Provider>
        )
        return new Map(map)
      }
      return map
    })
    option?.onUpdate?.(props)
  }

  dispatch((map) => {
    const handler = { unmount, update }
    map.set(
      uuid,
      <MountContext.Provider key={uuid} value={handler}>
        {React.cloneElement(element, handler as any)}
      </MountContext.Provider>
    )
    return new Map(map)
  })

  return {
    unmount,
    update,
  }
}

/**
 * 自动关联当前组件生命周期，自动移除挂载组件
 * @param element
 * @returns
 */
export const useMount = () => {
  const instances = useRef<ReturnType<typeof mount<any>>[]>([])
  useEffect(() => {
    return () => {
      instances.current.forEach((instance) => {
        instance.unmount()
      })
    }
  }, [])

  return <P,>(
    element: React.ReactElement<P & { key?: string }>,
    option?: IMountOption
  ) => {
    const target = mount<P>(element, {
      onUnmount: () => {
        instances.current = instances.current.filter((i) => i !== target)
        option?.onUnmount?.()
      },
    })

    instances.current.push(target)
    return target
  }
}

export const useMountMethod = () => {
  return React.useContext(MountContext)
}

export default Render
